import React, { useState, useEffect } from 'react';
import { dataService } from '../services/dataService';
import { ScheduleEditor, scheduleLabel, normaliseSchedule } from './Pipeline/ExecutionPanel';
import { useWorkspaceRole } from '../contexts/WorkspaceRoleContext';

const DEFAULT_EXEC = {
  active: false,
  scheduleConfig: { type: 'triggered', days: [1,2,3,4,5], from: '09:00', to: '17:00' },
};

function loadExecConfig(workspaceId) {
  if (!workspaceId) return null;
  try { return JSON.parse(localStorage.getItem(`pipelineExecution_${workspaceId}`)); } catch { return null; }
}

function saveExecConfig(workspaceId, config) {
  if (!workspaceId) return;
  localStorage.setItem(`pipelineExecution_${workspaceId}`, JSON.stringify(config));
}

function seededTokens(nodeId) {
  let h = 0;
  for (let i = 0; i < nodeId.length; i++) { h = Math.imul(31, h) + nodeId.charCodeAt(i) | 0; }
  return ((h >>> 0) % 8000) + 500;
}

function fmtTokens(n) { return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`; }

function StatusBadge({ active, hasSchedule }) {
  if (!hasSchedule && !active) return <span className="schedules-fn-badge no-schedule">No schedule</span>;
  if (!active) return <span className="schedules-fn-badge inactive">Inactive</span>;
  return <span className="schedules-fn-badge active">Active</span>;
}

export default function SchedulesView({ workspaceId }) {
  const { hasCapability } = useWorkspaceRole();
  const canEdit = hasCapability('edit_pipeline');
  const [nodes,       setNodes]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [execConfig,  setExecConfig]  = useState(DEFAULT_EXEC);
  const [editPipeline, setEditPipeline] = useState(false);
  const [expandedFns, setExpandedFns] = useState({});

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    dataService.getPipeline(workspaceId).then(data => {
      setNodes(data.nodes || []);
      setLoading(false);
    });
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    const saved = loadExecConfig(workspaceId);
    if (saved) setExecConfig({ ...DEFAULT_EXEC, ...saved });
  }, [workspaceId]);

  const updateExecConfig = (patch) => {
    setExecConfig(prev => {
      const next = { ...prev, ...patch };
      saveExecConfig(workspaceId, next);
      return next;
    });
  };

  const functionNodes = nodes.filter(n => n.type === 'functionNode');
  const pipelineSched = execConfig.scheduleConfig;

  const hasWarning = (node) => {
    const active = node.data.active !== false;
    if (!active) return false;
    const rawSched = node.data.scheduleConfig != null ? node.data.scheduleConfig : node.data.schedule;
    const cfg = normaliseSchedule(rawSched);
    const effectiveType = cfg ? cfg.type : (pipelineSched?.type || 'triggered');
    return effectiveType === 'triggered' && (pipelineSched?.type || 'triggered') === 'triggered';
  };

  if (loading) return <div className="schedules-view"><div className="pipeline-loading">Loading schedules…</div></div>;

  return (
    <div className="schedules-view">
      <h1 className="schedules-heading">Schedules</h1>

      {!canEdit && (
        <div style={{ color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', fontSize: 12, fontWeight: 600, marginBottom: 14 }}>
          🔒 You have read-only access. Editing schedules requires the pipeline-edit permission.
        </div>
      )}

      {/* ── Pipeline card ── */}
      <div className="schedules-pipeline-card">
        <div className="schedules-pipeline-top">
          <div className="schedules-pipeline-left">
            <div className={`schedules-status-dot${execConfig.active ? ' on' : ''}`} />
            <div>
              <div className="schedules-pipeline-title">Pipeline schedule</div>
              <div className="schedules-pipeline-summary">{scheduleLabel(pipelineSched)}</div>
            </div>
          </div>
          <div className="schedules-pipeline-right">
            <label className="exec-toggle-row" style={{ gap: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {execConfig.active ? 'Active' : 'Inactive'}
              </span>
              <span
                className={`exec-toggle${execConfig.active ? ' on' : ''}${!canEdit ? ' disabled' : ''}`}
                onClick={() => canEdit && updateExecConfig({ active: !execConfig.active })}
              >
                <span className="exec-toggle-knob" />
              </span>
            </label>
            {canEdit && (
              <button className="schedules-edit-btn" onClick={() => setEditPipeline(e => !e)}>
                {editPipeline ? 'Done' : 'Edit'}
              </button>
            )}
          </div>
        </div>

        {editPipeline && (
          <div className="schedules-pipeline-editor">
            <ScheduleEditor
              value={pipelineSched || DEFAULT_EXEC.scheduleConfig}
              onChange={(cfg) => updateExecConfig({ scheduleConfig: cfg })}
              allowNull={false}
            />
          </div>
        )}
      </div>

      {/* ── Function list ── */}
      <div className="schedules-fn-list">
        {functionNodes.length === 0 && (
          <div className="schedules-empty">No function nodes found in this pipeline.</div>
        )}

        {functionNodes.map(node => {
          const active   = node.data.active !== false;
          const rawSched = node.data.scheduleConfig != null ? node.data.scheduleConfig : node.data.schedule;
          const schedCfg = normaliseSchedule(rawSched);
          const tokens   = seededTokens(node.id);
          const warn     = hasWarning(node);
          const isOpen   = !!expandedFns[node.id];

          let schedSummary;
          if (!active)      schedSummary = 'Inactive';
          else if (schedCfg) schedSummary = scheduleLabel(schedCfg);
          else              schedSummary = `Follows pipeline: ${scheduleLabel(pipelineSched)}`;

          return (
            <div key={node.id} className={`schedules-fn-card${!active ? ' inactive' : ''}`}>
              <div className="schedules-fn-main">
                <div className="schedules-fn-left">
                  <span className="schedules-fn-icon">⬡</span>
                  <div>
                    <div className="schedules-fn-name">{node.data.label || 'Unnamed'}</div>
                    <StatusBadge active={active} hasSchedule={!!schedCfg} />
                  </div>
                </div>

                <div className="schedules-fn-mid">
                  <span className="schedules-fn-sched-text">{schedSummary}</span>
                  {warn && <span className="schedules-warning">⚠ No auto-trigger</span>}
                </div>

                <div className="schedules-fn-right">
                  <span className="schedules-fn-tokens">{fmtTokens(tokens)} tok</span>
                  {canEdit && (
                    <button
                      className="schedules-fn-expand-btn"
                      onClick={() => setExpandedFns(prev => ({ ...prev, [node.id]: !prev[node.id] }))}
                    >
                      {isOpen ? '▲' : '▼'}
                    </button>
                  )}
                </div>
              </div>

              {isOpen && (
                <div className="schedules-fn-editor-wrap">
                  <ScheduleEditor
                    value={schedCfg}
                    onChange={(v) => {
                      setNodes(prev => prev.map(n =>
                        n.id === node.id ? { ...n, data: { ...n.data, scheduleConfig: v, schedule: null } } : n
                      ));
                      dataService.getPipeline(workspaceId).then(data => {
                        const updated = (data.nodes || []).map(n =>
                          n.id === node.id ? { ...n, data: { ...n.data, scheduleConfig: v, schedule: null } } : n
                        );
                        dataService.savePipeline(workspaceId, updated, data.edges || []);
                      });
                    }}
                    allowNull={true}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
